/**
 * Dashboard leveranciersbeoordelingen (gap-analyse #7): kerncijfers, verdeling per
 * categorie en een kaart per leverancier met gemiddelde score en laatste conclusie.
 * Filters op categorie, functie, leverancier en jaar. Daaronder de lopende kwartaalronde
 * (wie moet nog invullen) uit het bestaande /admin/overview.
 * Recht: admin_settings (view), zoals het endpoint; ronde openen: admin_settings (edit).
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert, Box, Button, CircularProgress, MenuItem, Paper, Table, TableBody, TableCell, TableHead,
  TableRow, TextField, Typography,
} from '@mui/material';
import toast from 'react-hot-toast';
import { BACKEND_URL } from '../../utils/apiConfig';
import { useUserRole } from '../../contexts/UserRoleContext';
import { bevestig } from '../../utils/bevestig';
import { meldFout } from '../../utils/apiFout';
import { CATEGORIEEN, alleFuncties, datumNl, filterBeoordelingen, vatBeoordelingenSamen } from './hrRekenen';
import { INK, Kerncijfer, Verdeling, bijgewerktTekst, haalJson } from './hrUi';

const API = `${BACKEND_URL}/Api/leverancier-reviews`;
const OORDEEL = { positief: 'Positief', neutraal: 'Neutraal', negatief: 'Negatief' };
const kwartaal = (q) => (q ? `Q${String(q).split('-Q')[1]} ${String(q).split('-Q')[0]}` : '');

function Ronde() {
  const { permissions } = useUserRole();
  const magOpenen = !!permissions?.admin_settings?.edit;
  const [o, setO] = useState(null);
  const [bezig, setBezig] = useState(false);
  const laad = useCallback(() => haalJson(`${API}/admin/overview`, 'Ronde laden').then(setO).catch((e) => meldFout(e, 'Ronde laden')), []);
  useEffect(() => { laad(); }, [laad]);

  const open = async () => {
    if (!(await bevestig({
      titel: 'Beoordelingsronde (her)berekenen?',
      tekst: 'De ronde over het afgelopen kwartaal wordt opnieuw opgebouwd uit draaidagen en taken. Projectleiders zien daarna hun openstaande beoordelingen; al ingevulde blijven staan.',
      knop: 'Berekenen',
    }))) return;
    setBezig(true);
    try {
      await haalJson(`${API}/open-campaign`, 'Ronde openen', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: '{}' });
      toast.success('Ronde bijgewerkt');
      laad();
    } catch (e) {
      meldFout(e, 'Ronde openen');
    } finally {
      setBezig(false);
    }
  };

  const pls = o?.pls || [];
  const tot = pls.reduce((a, p) => ({ done: a.done + (p.done || 0), total: a.total + (p.total || 0) }), { done: 0, total: 0 });
  return (
    <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, borderColor: INK.lijn, mt: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
        <Typography sx={{ fontWeight: 700, color: INK.tekst, flex: 1 }}>
          {o?.quarter_id ? `Lopende ronde ${kwartaal(o.quarter_id)} — ${tot.done} van ${tot.total} ingevuld` : 'Nog geen beoordelingsronde'}
        </Typography>
        {magOpenen && <Button onClick={open} disabled={bezig} sx={{ textTransform: 'none' }}>{bezig ? 'Bezig…' : 'Ronde (her)berekenen'}</Button>}
      </Box>
      {pls.length > 0 && (
        <Table size="small" sx={{ '& th': { color: INK.zacht, fontWeight: 700, fontSize: 12 }, '& td': { fontSize: 13 } }}>
          <TableHead><TableRow><TableCell>Projectleider</TableCell><TableCell align="right">Ingevuld</TableCell><TableCell align="right">Open</TableCell><TableCell align="right">Niet te koppelen</TableCell></TableRow></TableHead>
          <TableBody>
            {pls.map((p) => (
              <TableRow key={p.pl_person_id}>
                <TableCell>{p.pl_name || p.pl_email}</TableCell>
                <TableCell align="right">{p.done || 0}</TableCell>
                <TableCell align="right">{p.pending || 0}</TableCell>
                <TableCell align="right" sx={{ color: INK.stil }}>{p.unresolved || 0}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </Paper>
  );
}

export default function BeoordelingenDashboard() {
  const [d, setD] = useState(null);
  const [fout, setFout] = useState(null);
  const [f, setF] = useState({ categorie: '', functie: '', leverancier: '', periode: '' });

  const laad = useCallback(async () => {
    setFout(null);
    try { setD(await haalJson(`${API}/dashboard`, 'Beoordelingen laden')); }
    catch (e) { setFout(e.message); }
  }, []);
  useEffect(() => { laad(); }, [laad]);

  const alle = useMemo(() => d?.beoordelingen || [], [d]);
  const functies = useMemo(() => alleFuncties(alle), [alle]);
  const leveranciers = useMemo(() => {
    const m = new Map();
    alle.forEach((b) => b.leverancierId && m.set(b.leverancierId, b.leverancier));
    return [...m.entries()].sort((a, b) => a[1].localeCompare(b[1], 'nl'));
  }, [alle]);
  const jaren = useMemo(() => [...new Set(alle.map((b) => (b.datum || '').slice(0, 4)).filter(Boolean))].sort().reverse(), [alle]);
  const s = useMemo(() => vatBeoordelingenSamen(filterBeoordelingen(alle, f)), [alle, f]);
  const zet = (k) => (e) => setF((x) => ({ ...x, [k]: e.target.value }));

  if (fout) return <Alert severity="error" action={<Button color="inherit" size="small" onClick={laad}>Opnieuw</Button>}>{fout}</Alert>;
  if (!d) return <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>;

  const k = s.kerncijfers;
  return (
    <>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, mb: 3, alignItems: 'center' }}>
        <TextField select size="small" label="Categorie" value={f.categorie} onChange={zet('categorie')} sx={{ width: 140 }}>
          <MenuItem value="">Alle</MenuItem>
          {CATEGORIEEN.map((c) => <MenuItem key={c} value={c}>{c}</MenuItem>)}
        </TextField>
        <TextField select size="small" label="Functie" value={f.functie} onChange={zet('functie')} sx={{ width: 190 }}>
          <MenuItem value="">Alle functies</MenuItem>
          {functies.map((x) => <MenuItem key={x} value={x}>{x}</MenuItem>)}
        </TextField>
        <TextField select size="small" label="Leverancier" value={f.leverancier} onChange={zet('leverancier')} sx={{ width: 220 }}>
          <MenuItem value="">Alle leveranciers</MenuItem>
          {leveranciers.map(([id, naam]) => <MenuItem key={id} value={id}>{naam}</MenuItem>)}
        </TextField>
        <TextField select size="small" label="Jaar" value={f.periode} onChange={zet('periode')} sx={{ width: 110 }}>
          <MenuItem value="">Alles</MenuItem>
          {jaren.map((j) => <MenuItem key={j} value={j}>{j}</MenuItem>)}
        </TextField>
        <Typography sx={{ fontSize: 12, color: INK.stil, ml: 'auto' }}>{bijgewerktTekst(d.bijgewerkt)}</Typography>
      </Box>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '2fr 1fr' }, gap: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignContent: 'flex-start' }}>
          <Kerncijfer label="Uitgevoerd" waarde={k.uitgevoerd} toelichting="beoordelingen" />
          <Kerncijfer label="Positief" waarde={k.positief} toelichting="score 4 of hoger" />
          <Kerncijfer label="Neutraal" waarde={k.neutraal} toelichting="score 3 tot 4" />
          <Kerncijfer label="Negatief" waarde={k.negatief} toelichting="score onder 3" />
        </Box>
        <Verdeling titel="Verdeling per categorie" rijen={s.verdeling} />
      </Box>

      {s.leveranciers.length === 0 ? (
        <Typography sx={{ color: INK.zacht }}>Geen beoordelingen bij deze filters.</Typography>
      ) : (
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 2 }}>
          {s.leveranciers.map((l) => (
            <Paper key={l.id} variant="outlined" sx={{ p: 2, borderRadius: 2, borderColor: INK.lijn }}>
              <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
                <Typography sx={{ fontWeight: 700, color: INK.tekst, flex: 1 }} noWrap>{l.naam}</Typography>
                <Typography sx={{ fontWeight: 800, fontSize: 20, color: INK.tekst, fontVariantNumeric: 'tabular-nums' }}>
                  {l.score === null ? '—' : l.score.toFixed(1)}
                </Typography>
              </Box>
              <Typography sx={{ fontSize: 12, color: INK.zacht, mb: 1 }}>
                {[l.functies.join(', '), OORDEEL[l.oordeel], `${l.aantal}× beoordeeld`].filter(Boolean).join(' · ')}
              </Typography>
              {l.laatste?.conclusie ? (
                <Typography sx={{ fontSize: 13, color: INK.tekst, display: '-webkit-box', WebkitLineClamp: 4, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                  “{l.laatste.conclusie}”
                </Typography>
              ) : (
                <Typography sx={{ fontSize: 13, color: INK.stil }}>Geen conclusie ingevuld.</Typography>
              )}
              <Typography sx={{ fontSize: 12, color: INK.stil, mt: 1 }}>
                Laatst: {datumNl(l.laatste?.datum)}{l.laatste?.ingevuldDoor ? ` door ${l.laatste.ingevuldDoor}` : ''}
              </Typography>
            </Paper>
          ))}
        </Box>
      )}

      <Ronde />
    </>
  );
}
